import type { AskYogiContextChip } from 'features/ask-yogi/types'
import type { ChartPageContext } from 'features/charts-v2/types/chart-page-context'
import type { ChartsPageState } from 'features/charts-v2/types/yogiql-page-state'
import type { AskYogiDashboardContext as DashboardPageContext } from 'features/dashboards-v2-1/types/dashboard-page-context'
import type { FeedbackPageState } from 'features/feedback/types/yogiql-page-state'
import type { SharedUser } from 'features/share/types'
export const STARTER_QUESTIONS_QUERY_KEY = 'ask-yogi-starter-questions'
export const CONVERSATIONS_QUERY_KEY = 'ask-yogi-conversations'
export const ASK_YOGI_CONNECTORS_QUERY_KEY = 'ask-yogi-connectors'
export const SAVED_PROMPTS_QUERY_KEY = 'ask-yogi-saved-prompts'
export const PROMPT_SHARES_QUERY_KEY = 'ask-yogi-prompt-shares'

export type AskYogiCapability = 'web_search'
export type AskYogiConnector = 'shopify' | 'monday' | 'asana'
export type AskYogiConnectorStatus = 'connected' | 'not_connected'

export interface AskYogiConnectorState {
  slug: AskYogiConnector
  name: string
  scope: 'org' | 'user'
  auth: 'credentials' | 'oauth'
  status: AskYogiConnectorStatus
  hubEnabled: boolean
}

export interface AskYogiConnectorStateDto {
  slug: AskYogiConnector
  name: string
  scope: 'org' | 'user'
  auth: 'credentials' | 'oauth'
  status: AskYogiConnectorStatus
  hub_enabled: boolean
}

const CONNECTOR_DISPLAY_NAMES: Record<AskYogiConnector, string> = {
  asana: 'Asana',
  monday: 'Monday.com',
  shopify: 'Shopify',
}

export const toAskYogiConnectorState = (
  dto: AskYogiConnectorStateDto,
): AskYogiConnectorState => ({
  slug: dto.slug,
  name: CONNECTOR_DISPLAY_NAMES[dto.slug],
  scope: dto.scope,
  auth: dto.auth,
  status: dto.status,
  hubEnabled: dto.hub_enabled,
})

/**
 * Defense-in-depth gate for the web-search capability: only emit
 * `['web_search']` when the web_search feature flag AND the toggle are on.
 * Gating here (not only at render) ensures a stale-true toggle never leaks
 * `capabilities` under the wrong rollout flag. Returns `[]` (omitted on the
 * wire) otherwise.
 */
export const deriveRequestCapabilities = ({
  isV2,
  isWebSearchEnabled,
  webSearchEnabled,
}: {
  isV2: boolean
  isWebSearchEnabled: boolean
  webSearchEnabled: boolean
}): AskYogiCapability[] =>
  isV2 && isWebSearchEnabled && webSearchEnabled ? ['web_search'] : []

export const deriveRequestConnectors = ({
  isV2,
  isConnectorsEnabled,
  selectedConnectors,
  availableConnectors,
}: {
  isV2: boolean
  isConnectorsEnabled: boolean
  selectedConnectors: ReadonlySet<AskYogiConnector>
  availableConnectors: AskYogiConnectorState[]
}): AskYogiConnector[] => {
  if (!isV2 || !isConnectorsEnabled) return []
  return availableConnectors
    .filter(
      (connector) =>
        connector.hubEnabled &&
        connector.status === 'connected' &&
        selectedConnectors.has(connector.slug),
    )
    .map((connector) => connector.slug)
}

export type AskYogiConnectorTrackingSlug = AskYogiConnector | 'web'

export const deriveConnectorsEnabled = ({
  isV2,
  isWebSearchEnabled,
  webSearchEnabled,
  isConnectorsEnabled,
  selectedConnectors,
  availableConnectors,
}: {
  isV2: boolean
  isWebSearchEnabled: boolean
  webSearchEnabled: boolean
  isConnectorsEnabled: boolean
  selectedConnectors: ReadonlySet<AskYogiConnector>
  availableConnectors: AskYogiConnectorState[]
}): AskYogiConnectorTrackingSlug[] => [
  ...(deriveRequestCapabilities({
    isV2,
    isWebSearchEnabled,
    webSearchEnabled,
  }).includes('web_search')
    ? (['web'] as const)
    : []),
  ...deriveRequestConnectors({
    isV2,
    isConnectorsEnabled,
    selectedConnectors,
    availableConnectors,
  }),
]

/**
 * Flat body of `POST /ask-yogi/conversations/{uuid}/messages`. The
 * conversation rides in the path, so it is NOT a body field.
 */
export interface SendMessageRequestBody {
  message: string | null
  attachment_uuids?: string[]
  capabilities?: AskYogiCapability[]
  connectors?: AskYogiConnector[]
  chart_context?: AskYogiChartContext
  dashboard_context?: AskYogiDashboardContext
  context_chip?: AskYogiContextChip
  skills?: string[]
  skill_context?: SkillContext
}

/** Per-skill context the server renders, hidden, into the turn's prompt. */
export type SkillContext = Record<string, Record<string, unknown>>

/** Each page a "View in ..." link opens, and the YogiQL state its page-state
 *  route stores. */
export type LinkPageStates = {
  charts: ChartsPageState
  feedback: FeedbackPageState
}

export type LinkPage = keyof LinkPageStates

export interface AttachmentUploadDto {
  attachment_uuid: string
}

export interface AskYogiAttachment {
  uuid: string
}

export const toAskYogiAttachment = (
  dto: AttachmentUploadDto,
): AskYogiAttachment => ({
  uuid: dto.attachment_uuid,
})

export type SavedPromptPermission = 'view' | 'edit'

export interface SavedPromptDto {
  uuid: string
  title: string
  prompt_text: string
  owner_user_id: number
  is_owner: boolean
  permission: SavedPromptPermission | null
  create_time: string
  update_time: string
  owner_name: string | null
  owner_initials: string | null
  owner_email: string | null
}

export interface SavedPrompt {
  uuid: string
  title: string
  promptText: string
  ownerUserId: number
  isOwner: boolean
  permission: SavedPromptPermission | null
  createTime: string
  updateTime: string
  ownerName: string | null
  ownerInitials: string | null
  ownerEmail: string | null
}

export const toSavedPrompt = (dto: SavedPromptDto): SavedPrompt => ({
  uuid: dto.uuid,
  title: dto.title,
  promptText: dto.prompt_text,
  ownerUserId: dto.owner_user_id,
  isOwner: dto.is_owner,
  ownerName: dto.owner_name,
  ownerInitials: dto.owner_initials,
  ownerEmail: dto.owner_email,
  permission: dto.permission,
  createTime: dto.create_time,
  updateTime: dto.update_time,
})

export interface GenerateTemplateResponseDto {
  template: string
}

export interface HubMember {
  userId: number
  email: string
  firstName: string | null
  lastName: string | null
}

/** A `/project/shared` user (already filtered to who the caller may share
 * with) as a prompt-share picker member; keyed by id for `recipient_user_id`. */
export const toHubMember = (user: SharedUser): HubMember => ({
  userId: user.id,
  email: user.email,
  firstName: user.first_name,
  lastName: user.last_name,
})

export interface PromptShareDto {
  recipient_user_id: number
  permission: SavedPromptPermission
  granted_by_user_id: number | null
  create_time: string
}

export interface PromptShare {
  recipientUserId: number
  permission: SavedPromptPermission
  grantedByUserId: number | null
  createTime: string
}

export const toPromptShare = (dto: PromptShareDto): PromptShare => ({
  recipientUserId: dto.recipient_user_id,
  permission: dto.permission,
  grantedByUserId: dto.granted_by_user_id,
  createTime: dto.create_time,
})

export interface DuplicateConversationRequest {
  /**
   * Fork anchor — the question being re-asked. Omitted to copy the whole
   * conversation; set to copy only the messages strictly BEFORE it, so the
   * caller can edit and resend that question against the earlier context.
   */
  before_message_uuid?: string
}

/**
 * Where the question was asked from, and what was on screen.
 *
 * Only the charts page sends this. Its presence tells the backend the request
 * came from there, which is one of the two gates on the conformant chart tool
 * surface (the other is the per-user flag). `panels` is empty while the page
 * has no chart drawn — still the charts page, still sent.
 */
export type AskYogiChartContext = ChartPageContext

/** Sent from a 2.1 dashboard for Ask Yogi Everywhere users; never alongside
 *  `chart_context`. */
export type AskYogiDashboardContext = DashboardPageContext
